import { clsx } from 'clsx'
import { useContext } from 'react'

import type { TypeComponentCtaButtonEntry } from '@contentful-types/component-cta-button'

import {
  ComponentCtaButtonType,
  CtaDescriptionPosition,
  CtaDescriptionWidth,
} from '@constants/enums/cta'

import { CompactCtaContext } from '@contexts/compact-cta-context'
import { CtaButtonContext } from '@contexts/cta-button-context'

import { ComponentLink } from '@components/component-link/component-link'
import {
  renderRichTextMultiLineContent,
  renderRichTextSingleLineContent,
} from '@components/renderer/rich-text'

import type { CtaProps } from '../cta-prop-types'

import { SigninCtaButton, SignupCtaButton } from './auth/auth'
import { CodaCtaButton } from './coda/coda'
import { GoCtaButton } from './go/go'
import { GrammarlyCtaButton } from './grammarly/grammarly'
import { LinkButton } from './link/link'
import { MailCtaButton } from './mail/mail'

import styles from './cta-button.module.scss'

export function CtaButtonContent({
  data,
  className,
}: {
  data: TypeComponentCtaButtonEntry
  className?: string
}) {
  const {
    fields: { type, ctaText, category, targetLink },
    sys: { id },
  } = data

  const isCompact = useContext(CompactCtaContext)
  const buttonText = renderRichTextSingleLineContent(ctaText)

  const ctaProps = {
    className: clsx(className),
    category,
    isCompact,
    'data-vqa': id,
  } as CtaProps

  switch (type) {
    case ComponentCtaButtonType.Link: {
      ctaProps.text = buttonText
      return targetLink ? <LinkButton targetLink={targetLink} {...ctaProps} /> : null
    }
    case ComponentCtaButtonType.Signup: {
      return <SignupCtaButton {...ctaProps} text={buttonText} />
    }
    case ComponentCtaButtonType.Signin: {
      return <SigninCtaButton {...ctaProps} text={buttonText} />
    }
    case ComponentCtaButtonType.Go: {
      return <GoCtaButton {...ctaProps} />
    }
    case ComponentCtaButtonType.Mail: {
      return <MailCtaButton {...ctaProps} />
    }
    case ComponentCtaButtonType.Coda: {
      return <CodaCtaButton {...ctaProps} />
    }
    case ComponentCtaButtonType.Grammarly: {
      return <GrammarlyCtaButton {...ctaProps} />
    }
    // No default
  }

  return null
}

export function CtaButtonDescription({ data }: { data: TypeComponentCtaButtonEntry }) {
  const {
    fields: { ctaUnderDescription },
  } = data

  const isFixedWidthDescription =
    useContext(CtaButtonContext).descriptionWidth === CtaDescriptionWidth.Fixed
  const ctaDescriptionText = ctaUnderDescription
    ? renderRichTextMultiLineContent(ctaUnderDescription)
    : null

  if (!ctaDescriptionText) {
    return null
  }

  return (
    <p
      className={clsx(styles.ctaDescription, {
        [styles.fixedWidthDescription]: isFixedWidthDescription,
      })}
    >
      {ctaDescriptionText}
    </p>
  )
}

export function CtaButton({
  data,
  inline,
}: {
  data: TypeComponentCtaButtonEntry
  inline?: boolean
}) {
  const {
    fields: { ctaUnderLink, internalName, category, type },
  } = data

  const { descriptionPosition, showCtaDescription } = useContext(CtaButtonContext)
  const ctaDescriptionPositionClass = {
    [CtaDescriptionPosition.Center]: styles.center,
    [CtaDescriptionPosition.Left]: styles.left,
  }[descriptionPosition]

  return (
    <div
      className={clsx(styles.ctaContainer, ctaDescriptionPositionClass, inline && styles.inline)}
      data-component-id={internalName}
    >
      {/* ctaButtonContainer className is needed for CtaButtonGroup column and banner styling */}
      <div className={clsx(styles.ctaButtonAndUnderLinkContainer, 'ctaButtonContainer')}>
        <CtaButtonContent data={data} />

        {ctaUnderLink &&
          type !== ComponentCtaButtonType.Mail &&
          type !== ComponentCtaButtonType.Go && (
            <div className={clsx(styles.ctaUnderLink)}>
              <ComponentLink targetLink={ctaUnderLink} category={category} />
            </div>
          )}
      </div>
      {showCtaDescription && <CtaButtonDescription data={data} />}
    </div>
  )
}
